import {computed} from 'vue';
import {usePage} from '@inertiajs/vue3';
import {COMMON_LOCALES, isCommonLocale} from '@/Constants/commonLocales';
import designApprovalDefaults from '../../data/design-approval-defaults.json';

export type DesignApprovalTranslation = {
    title: string;
    text: string;
    checkbox_label: string;
};

export type DesignApprovalTranslations = Record<string, DesignApprovalTranslation>;

const DEFAULT_TRANSLATIONS = designApprovalDefaults as DesignApprovalTranslations;

const emptyTranslation = (): DesignApprovalTranslation => ({
    title: '',
    text: '',
    checkbox_label: '',
});

const hasContent = (translation: DesignApprovalTranslation | undefined): boolean => (
    Boolean(translation?.title?.trim() || translation?.text?.trim() || translation?.checkbox_label?.trim())
);

const resolveTranslation = (
    translations: DesignApprovalTranslations | null | undefined,
    locale: string,
): DesignApprovalTranslation => {
    const fallbackLocales = [
        locale,
        'nl',
        'en',
        ...Object.keys(translations ?? {}),
    ].filter((value, index, items) => isCommonLocale(value) && items.indexOf(value) === index);

    for (const fallbackLocale of fallbackLocales) {
        const translation = translations?.[fallbackLocale];

        if (!hasContent(translation)) {
            continue;
        }

        const hardcodedFallback = DEFAULT_TRANSLATIONS[fallbackLocale] ?? DEFAULT_TRANSLATIONS.nl;

        return {
            title: translation?.title?.trim() || hardcodedFallback.title,
            text: translation?.text?.trim() || hardcodedFallback.text,
            checkbox_label: translation?.checkbox_label?.trim() || hardcodedFallback.checkbox_label,
        };
    }

    const hardcodedFallback = DEFAULT_TRANSLATIONS[locale]
        ?? DEFAULT_TRANSLATIONS.nl
        ?? emptyTranslation();

    return hardcodedFallback;
};

export const useDesignApprovalContent = () => {
    const page = usePage();

    const locale = computed(() => {
        const currentLocale = page.props.locale;

        return isCommonLocale(currentLocale) ? currentLocale : 'nl';
    });

    const team = computed(() => page.props.team as {
        design_approval_translations?: DesignApprovalTranslations;
    } | null | undefined);

    const content = computed(() => resolveTranslation(
        team.value?.design_approval_translations,
        locale.value,
    ));

    return {
        content,
        locale,
    };
};

export const createEmptyDesignApprovalTranslations = (
    source?: DesignApprovalTranslations | null,
): DesignApprovalTranslations => {
    const translations: DesignApprovalTranslations = {...DEFAULT_TRANSLATIONS};

    COMMON_LOCALES.forEach(({code}) => {
        translations[code] = resolveTranslation(source, code);
    });

    return translations;
};

export const ensureDesignApprovalLocale = (
    translations: DesignApprovalTranslations,
    locale: string,
): DesignApprovalTranslation => {
    if (!translations[locale]) {
        translations[locale] = DEFAULT_TRANSLATIONS[locale] ?? emptyTranslation();
    }

    return translations[locale];
};